import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { inputCls, labelCls } from "./shared";

const EMPTY_FORM = { name: "", description: "", vimeo_url: "", is_live: false };

export default function RoomsTab() {
  const [rooms, setRooms] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/rooms").then((r) => setRooms(r.data)).catch((e) => console.error("Falha ao carregar salas:", e));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/rooms/${editing}`, form);
        toast.success("Sala atualizada");
      } else {
        await api.post("/admin/rooms", form);
        toast.success("Sala criada");
      }
      setForm(EMPTY_FORM);
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta sala e seu chat?")) return;
    await api.delete(`/admin/rooms/${id}`);
    toast.success("Sala excluída");
    load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <form onSubmit={save} className="card-metric space-y-4 h-fit">
        <h3 className="text-lg font-semibold text-white">{editing ? "Editar sala" : "Nova sala"}</h3>
        <div>
          <label className={labelCls}>Nome da sala</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={inputCls} placeholder="Ex: Plenária Principal" data-testid="room-name-input" />
        </div>
        <div>
          <label className={labelCls}>Descrição</label>
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className={inputCls} placeholder="Ex: Palestras magistrais" data-testid="room-description-input" />
        </div>
        <div>
          <label className={labelCls}>Link do Vimeo (transmissão ao vivo)</label>
          <input value={form.vimeo_url} onChange={(e) => setForm({ ...form, vimeo_url: e.target.value })}
            className={`${inputCls} font-data text-sm`} placeholder="https://vimeo.com/event/..." data-testid="room-vimeo-input" />
          <p className="text-[11px] text-slate-500 mt-1.5">Aceita link de vídeo gravado (vimeo.com/123...) ou de evento ao vivo (vimeo.com/event/123...) — o embed é gerado automaticamente.</p>
        </div>
        <label className="flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer">
          <input type="checkbox" checked={form.is_live} onChange={(e) => setForm({ ...form, is_live: e.target.checked })}
            className="w-4 h-4 accent-[#F5A623]" data-testid="room-live-checkbox" />
          Marcar como AO VIVO agora
        </label>
        <div className="flex gap-2">
          <button type="submit" className="btn-primary" data-testid="room-save-btn">{editing ? "Salvar" : "Criar sala"}</button>
          {editing && (
            <button type="button" className="btn-ghost" onClick={() => { setEditing(null); setForm(EMPTY_FORM); }}>
              Cancelar
            </button>
          )}
        </div>
      </form>

      <div className="space-y-3">
        {rooms.map((r) => (
          <div key={r.id} className="card-metric flex items-start justify-between gap-3" data-testid={`admin-room-${r.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-white">{r.name}</h4>
                {r.is_live && <span className="text-[10px] font-data uppercase bg-red-600/80 text-white px-2 py-0.5 rounded-full">Ao vivo</span>}
              </div>
              <p className="text-xs text-slate-400 mt-1 truncate">{r.description || "—"}</p>
              <p className="text-xs text-slate-500 font-data mt-1 truncate">{r.vimeo_url || "Sem link Vimeo"}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => { setEditing(r.id); setForm({ name: r.name, description: r.description, vimeo_url: r.vimeo_url, is_live: r.is_live }); }}
                className="btn-secondary !px-3 !py-1.5 text-xs" data-testid={`edit-room-${r.id}`}>Editar</button>
              <button onClick={() => remove(r.id)} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded" data-testid={`delete-room-${r.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
